import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MemberDetailsModal from './MemberDetailsModal';
import ViewShopModal from './ViewShopModal';
import { FiAlertCircle, FiX } from 'react-icons/fi';
import { format } from 'date-fns';

const ReferralListModal = ({ member, onClose }) => {
    if (!member) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">
                        Referrals by {member.godID} - {member.godName}
                    </h3>
                    <button 
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <FiX className="w-6 h-6" />
                    </button>
                </div>
                
                <div className="space-y-2">
                    {member.referrals.map((referral, index) => (
                        <div 
                            key={referral._id} 
                            className={`p-3 rounded-lg ${
                                referral.status === 'active' 
                                    ? 'bg-green-50 border border-green-100' 
                                    : 'bg-gray-50 border border-gray-100'
                            }`}
                        >
                            <div className="flex justify-between items-center">
                                <span className={`text-base ${
                                    referral.status === 'active' 
                                        ? 'text-green-700' 
                                        : 'text-gray-700'
                                }`}>
                                    {index + 1}. {referral.godID} - {referral.godName} - {referral.webID}
                                </span>
                                {referral.status !== 'active' && (
                                    <span className="text-sm px-2 py-1 rounded bg-gray-200 text-gray-600">
                                        {referral.status}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const MemberAnalytics = () => {
    const [topMembers, setTopMembers] = useState([]);
    const [topShops, setTopShops] = useState([]);
    const [topReferrers, setTopReferrers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedMember, setSelectedMember] = useState(null);
    const [selectedShop, setSelectedShop] = useState(null);
    const [loadingMember, setLoadingMember] = useState(false);
    const [selectedReferrer, setSelectedReferrer] = useState(null);

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                setLoading(true);
                setError('');
                const token = localStorage.getItem('token');
                const [membersResponse, shopsResponse, referrersResponse] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/api/godmembers/analytics/top-payments`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/shops/analytics/top-members`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/godmembers/analytics/top-referrers`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    })
                ]);

                if (membersResponse.data.success) {
                    setTopMembers(membersResponse.data.data);
                }
                if (shopsResponse.data.success) {
                    setTopShops(shopsResponse.data.data);
                }
                if (referrersResponse.data.success) {
                    console.log('Referrers data:', referrersResponse.data.data);
                    setTopReferrers(referrersResponse.data.data);
                }
            } catch (err) {
                console.error('Analytics fetch error:', err);
                setError(`Failed to fetch analytics: ${err.response?.data?.error || err.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, []);

    const handleMemberClick = async (member) => {
        try {
            setLoadingMember(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/godmembers/${member._id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                setSelectedMember(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching member details:', err);
            setError(`Failed to fetch member details: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoadingMember(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-SG', {
            style: 'currency',
            currency: 'SGD'
        }).format(value);
    };

    const getStatusIcon = (status) => {
        return status === 'active' ? '✅' : '❌';
    };

    if (loading) {
        return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-white rounded-lg shadow p-6">
                        <div className="animate-pulse">
                            <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                            <div className="space-y-3">
                                {[1, 2, 3, 4, 5].map((j) => (
                                    <div key={j} className="flex justify-between items-center">
                                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className="mt-6 p-4 bg-red-50 rounded-lg flex items-center text-red-700">
                <FiAlertCircle className="w-5 h-5 mr-2" />
                {error}
            </div>
        );
    }

    const EmptyState = ({ title }) => (
        <div className="text-center py-4">
            <p className="text-gray-500">No {title} data available</p>
        </div>
    );

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6 mb-8">
            {/* Left Column - Top 10 Members by Payment (Merged 2 Rows) */}
            <div className="lg:row-span-2 bg-blue-50 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium mb-4">Top 10 Members by Payment</h3>
                {topMembers.length > 0 ? (
                    <div className="space-y-3">
                        {topMembers.map((member, index) => (
                            <div
                                key={member._id}
                                onClick={() => handleMemberClick(member)}
                                className="flex justify-between items-center p-2 hover:bg-blue-100 rounded cursor-pointer transition duration-150"
                            >
                                <div className="flex items-center space-x-2">
                                    <span className="text-gray-800">
                                        {index + 1}. {member.fullName}
                                    </span>
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                        ${member.status === 'active' ? 'bg-green-100 text-green-800' : 
                                          member.status === 'expired' ? 'bg-red-100 text-red-800' :
                                          member.status === 'hold' ? 'bg-yellow-100 text-yellow-800' :
                                          member.status === 'stop' ? 'bg-rose-100 text-rose-800' :
                                          member.status === 'expiring' ? 'bg-orange-100 text-orange-800' :
                                          member.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                          member.status === 'changedName' ? 'bg-purple-100 text-purple-800' :
                                          'bg-gray-100 text-gray-800'}`}>
                                        {member.status}
                                    </span>
                                </div>
                                <span className="font-medium text-blue-600">
                                    {formatCurrency(member.totalPayments)}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <EmptyState title="member payment" />
                )}
            </div>

            {/* Right Column Top - Top 5 Referrals */}
            <div className="bg-yellow-50 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium mb-4">Top 5 Referrals</h3>
                {topReferrers.length > 0 ? (
                    <div className="space-y-3">
                        {topReferrers.map((member, index) => (
                            <div
                                key={member._id}
                                onClick={() => setSelectedReferrer(member)}
                                className="flex justify-between items-center p-2 hover:bg-yellow-100 rounded cursor-pointer transition duration-150"
                            >
                                <span className="text-gray-800 truncate flex-1 mr-2">
                                    {index + 1}. {member.godID} - {member.godName} - {member.webID}
                                </span>
                                <span className="font-medium text-yellow-600 whitespace-nowrap">
                                    {member.referralCount} referrals
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <EmptyState title="referral" />
                )}
            </div>

            {/* Right Column Bottom - Top 5 Shops */}
            <div className="bg-purple-50 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium mb-4">Top 5 Shops by Member Count</h3>
                {topShops.length > 0 ? (
                    <div className="space-y-3">
                        {topShops.map((shop, index) => (
                            <div
                                key={shop._id}
                                onClick={() => setSelectedShop(shop)}
                                className="flex justify-between items-center p-2 hover:bg-purple-100 rounded cursor-pointer transition duration-150"
                            >
                                <span className="text-gray-800 truncate flex-1 mr-2">
                                    {index + 1}. {shop.shopName} ({shop.region}, {shop.area})
                                </span>
                                <span className="font-medium text-purple-600 whitespace-nowrap">
                                    {shop.memberCount} active members
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <EmptyState title="shop" />
                )}
            </div>

            {/* Loading Overlay */}
            {loadingMember && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                </div>
            )}

            {/* Modals */}
            {selectedMember && (
                <MemberDetailsModal
                    member={selectedMember}
                    onClose={() => setSelectedMember(null)}
                />
            )}
            {selectedShop && (
                <ViewShopModal
                    shop={selectedShop}
                    onClose={() => setSelectedShop(null)}
                />
            )}
            {selectedReferrer && (
                <ReferralListModal
                    member={selectedReferrer}
                    onClose={() => setSelectedReferrer(null)}
                />
            )}
        </div>
    );
};

export default MemberAnalytics; 